import { apiClient } from './api-client';

// ============== Types ==============

export interface IntentEntity {
  type: string;
  value: string;
  confidence: number;
  start?: number;
  end?: number;
}

export interface IntentClassificationResponse {
  intent: string;
  confidence: number;
  entities: IntentEntity[];
  subIntent?: string;
}

export interface SentimentAspect {
  aspect: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  score: number;
  keywords?: string[];
}

export interface SentimentEmotion {
  emotion: string;
  intensity: number;
}

export interface SentimentAnalysisResponse {
  sentiment: 'positive' | 'negative' | 'neutral' | 'mixed';
  score: number;
  confidence: number;
  aspects?: SentimentAspect[];
  emotions?: SentimentEmotion[];
}

export interface TicketContext {
  ticketId: string;
  subject: string;
  description?: string;
  status?: string;
  priority?: string;
  channel?: string;
  tags?: string[];
}

export interface MessageContext {
  content: string;
  senderType: 'customer' | 'agent' | 'system';
  senderName?: string;
  timestamp?: string;
}

export interface CustomerContext {
  name?: string;
  email?: string;
  previousTickets?: number;
  segment?: string;
  sentiment?: string;
}

export interface SuggestionItem {
  content: string;
  confidence: number;
  tone: string;
  reasoning?: string;
}

export interface SimilarTicket {
  ticketId: string;
  subject: string;
  similarity: number;
  resolution?: string;
}

export interface RecommendedArticle {
  articleId: string;
  title: string;
  relevance: number;
  excerpt?: string;
  url?: string;
}

export interface SuggestionsResponse {
  suggestions: SuggestionItem[];
  similarTickets?: SimilarTicket[];
  recommendedArticles?: RecommendedArticle[];
  intent?: string;
  sentiment?: string;
}

export interface QuickAnalyzeResponse {
  intent: string;
  intentConfidence: number;
  sentiment: 'positive' | 'negative' | 'neutral' | 'mixed';
  sentimentScore: number;
  emotions?: SentimentEmotion[];
  suggestedPriority?: 'low' | 'medium' | 'high' | 'urgent';
  suggestedCategory?: string;
}

export interface ChatbotResponse {
  response: string;
  conversationId: string;
  confidence: number;
  needsHuman: boolean;
  suggestedArticles?: RecommendedArticle[];
  suggestedActions?: Array<{
    type: string;
    label: string;
    value?: string;
    url?: string;
  }>;
  escalationReason?: string;
}

export type ResponseTone =
  | 'professional'
  | 'empathetic'
  | 'apologetic'
  | 'enthusiastic'
  | 'technical';

// ============== Ticket-type classification (test/preview) ==============

export interface ClassifyTicketTypeOption {
  name: string;
  id?: string;
  description?: string;
  keywords?: string[];
  classificationConfig?: Record<string, any>;
  isCatchAll?: boolean;
}

export interface ClassifyTicketTypeResult {
  ticketTypeId: string | null;
  ticketTypeName: string | null;
  confidence: number;
  classificationMethod: string;
  alternativeTypes: Array<{ id: string; name: string; confidence: number }>;
}

// ============== Service ==============

class AiService {
  private readonly basePath = '/ai';

  // Ticket-type classification (used by the Ticket Type "Test classification" panel)
  async classifyTicketType(payload: {
    subject: string;
    description?: string;
    ticketTypes: ClassifyTicketTypeOption[];
    htmlContent?: string;
  }): Promise<ClassifyTicketTypeResult> {
    const response = await apiClient.post<ClassifyTicketTypeResult>(
      `${this.basePath}/classify/ticket-type`,
      payload,
    );
    return response.data;
  }

  /**
   * Translate text into English. Rejects when it could not be translated -
   * the caller shows that, never the original in its place.
   */
  async translateToEnglish(
    text: string,
  ): Promise<{ translatedText: string; sourceLanguage?: string; truncated: boolean }> {
    const response = await apiClient.post(
      `${this.basePath}/translate`,
      { text, targetLanguage: 'en' },
      { timeout: 120000 },
    );
    return response.data;
  }

  // Intent Classification
  async classifyIntent(
    message: string,
    context?: string,
    language?: string
  ): Promise<IntentClassificationResponse> {
    const response = await apiClient.post<IntentClassificationResponse>(
      `${this.basePath}/intent/classify`,
      { message, context, language }
    );
    return response.data;
  }

  async getAvailableIntents(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`${this.basePath}/intent/intents`);
    return response.data;
  }

  // Sentiment Analysis
  async analyzeSentiment(
    text: string,
    options?: { includeAspects?: boolean; language?: string }
  ): Promise<SentimentAnalysisResponse> {
    const response = await apiClient.post<SentimentAnalysisResponse>(
      `${this.basePath}/sentiment/analyze`,
      { text, ...options }
    );
    return response.data;
  }

  async getSentimentCategories(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`${this.basePath}/sentiment/categories`);
    return response.data;
  }

  async analyzeSentimentBatch(
    messages: Array<{ id: string; text: string }>
  ): Promise<Array<{ id: string; sentiment: SentimentAnalysisResponse }>> {
    const response = await apiClient.post<Array<{ id: string; sentiment: SentimentAnalysisResponse }>>(
      `${this.basePath}/sentiment/batch`,
      messages
    );
    return response.data;
  }

  // Response Suggestions
  async generateSuggestions(params: {
    ticket: TicketContext;
    messages: MessageContext[];
    customer?: CustomerContext;
    count?: number;
    tone?: ResponseTone;
    includeArticles?: boolean;
  }): Promise<SuggestionsResponse> {
    const response = await apiClient.post<SuggestionsResponse>(
      `${this.basePath}/suggestions/generate`,
      params
    );
    return response.data;
  }

  async getResponseTones(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`${this.basePath}/suggestions/tones`);
    return response.data;
  }

  // Quick Analyze (Combined)
  async quickAnalyze(
    message: string,
    ticketId?: string
  ): Promise<QuickAnalyzeResponse> {
    const response = await apiClient.post<QuickAnalyzeResponse>(
      `${this.basePath}/analyze`,
      { message, ticketId }
    );
    return response.data;
  }

  // Chatbot
  async sendChatbotMessage(params: {
    message: string;
    conversationId?: string;
    customer?: CustomerContext;
    tone?: string;
    language?: string;
  }): Promise<ChatbotResponse> {
    const response = await apiClient.post<ChatbotResponse>(
      `${this.basePath}/chatbot/message`,
      params
    );
    return response.data;
  }

  async getChatbotCapabilities(): Promise<{
    supportedLanguages: string[];
    supportedTones: string[];
    features: string[];
  }> {
    const response = await apiClient.get<{
      supportedLanguages: string[];
      supportedTones: string[];
      features: string[];
    }>(`${this.basePath}/chatbot/capabilities`);
    return response.data;
  }

  // Utility methods
  getSentimentColor(sentiment: SentimentAnalysisResponse['sentiment']): string {
    const colors: Record<SentimentAnalysisResponse['sentiment'], string> = {
      positive: 'text-green-600',
      negative: 'text-red-600',
      neutral: 'text-gray-500',
      mixed: 'text-yellow-600',
    };
    return colors[sentiment] || colors.neutral;
  }

  getSentimentBgColor(sentiment: SentimentAnalysisResponse['sentiment']): string {
    const colors: Record<SentimentAnalysisResponse['sentiment'], string> = {
      positive: 'bg-green-100',
      negative: 'bg-red-100',
      neutral: 'bg-gray-100',
      mixed: 'bg-yellow-100',
    };
    return colors[sentiment] || colors.neutral;
  }

  getSentimentIcon(sentiment: SentimentAnalysisResponse['sentiment']): string {
    const icons: Record<SentimentAnalysisResponse['sentiment'], string> = {
      positive: '😊',
      negative: '😞',
      neutral: '😐',
      mixed: '🤔',
    };
    return icons[sentiment] || icons.neutral;
  }

  formatSentimentScore(score: number): string {
    if (score >= 0.5) return 'Very Positive';
    if (score >= 0.2) return 'Positive';
    if (score >= -0.2) return 'Neutral';
    if (score >= -0.5) return 'Negative';
    return 'Very Negative';
  }

  getIntentDisplayName(intent: string): string {
    const displayNames: Record<string, string> = {
      question: 'Question',
      complaint: 'Complaint',
      request_support: 'Support Request',
      request_feature: 'Feature Request',
      report_bug: 'Bug Report',
      request_refund: 'Refund Request',
      update_account: 'Account Update',
      cancel_service: 'Cancellation',
      get_information: 'Information Request',
      provide_feedback: 'Feedback',
      greeting: 'Greeting',
      farewell: 'Farewell',
      other: 'Other',
    };
    return displayNames[intent] || intent.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

export const aiService = new AiService();
